import {
    Building2, Check, Copy, KeyRound, ShieldCheck,
    Smartphone
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import {
    merchantAuthAPI
} from '../lib/api';
import { cabinetPath } from '../lib/domains';
import { isMerchantImpersonationActive } from '../lib/merchantSession';

export const MerchantSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, refreshUser, logout } = useMerchantAuth();
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [twoFactorDialog, setTwoFactorDialog] = useState<'setup' | 'disable' | null>(null);
    const [twoFactorSetup, setTwoFactorSetup] = useState<{
        otpauth_uri: string;
        manual_key: string;
    } | null>(null);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [twoFactorLoading, setTwoFactorLoading] = useState(false);
    const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
    const [twoFactorCardError, setTwoFactorCardError] = useState<string | null>(null);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorCode: '',
    });
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
    const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<string | null>(null);
    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const closeTwoFactorDialog = () => {
        setTwoFactorDialog(null);
        setTwoFactorSetup(null);
        setTwoFactorCode('');
        setTwoFactorError(null);
    };

    const handleTwoFactorToggle = async (enabled: boolean) => {
        setTwoFactorError(null);
        setTwoFactorCardError(null);
        setTwoFactorCode('');
        if (!enabled) {
            setTwoFactorDialog('disable');
            return;
        }

        setTwoFactorLoading(true);
        try {
            const setup = await merchantAuthAPI.setupTwoFactor();
            setTwoFactorSetup(setup);
            setTwoFactorDialog('setup');
        } catch (err: any) {
            setTwoFactorCardError(
                err.response?.data?.detail || 'Не удалось подготовить 2FA'
            );
        } finally {
            setTwoFactorLoading(false);
        }
    };

    const handleEnableTwoFactor = async () => {
        setTwoFactorLoading(true);
        setTwoFactorError(null);
        try {
            await merchantAuthAPI.enableTwoFactor(twoFactorCode);
            await refreshUser();
            closeTwoFactorDialog();
        } catch (err: any) {
            setTwoFactorError(err.response?.data?.detail || 'Не удалось включить 2FA');
        } finally {
            setTwoFactorLoading(false);
        }
    };

    const handleDisableTwoFactor = async () => {
        setTwoFactorLoading(true);
        setTwoFactorError(null);
        try {
            await merchantAuthAPI.disableTwoFactor(twoFactorCode);
            await refreshUser();
            closeTwoFactorDialog();
        } catch (err: any) {
            setTwoFactorError(err.response?.data?.detail || 'Не удалось выключить 2FA');
        } finally {
            setTwoFactorLoading(false);
        }
    };

    const handlePasswordChange = async (event: React.FormEvent) => {
        event.preventDefault();
        setPasswordChangeError(null);
        setPasswordChangeSuccess(null);

        if (passwordForm.newPassword.length < 10) {
            setPasswordChangeError('Новый пароль должен содержать минимум 10 символов');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordChangeError('Новые пароли не совпадают');
            return;
        }
        if (passwordForm.currentPassword === passwordForm.newPassword) {
            setPasswordChangeError('Новый пароль должен отличаться от текущего');
            return;
        }
        if (user?.two_factor_enabled && passwordForm.twoFactorCode.length !== 6) {
            setPasswordChangeError('Введите шестизначный код Google Authenticator');
            return;
        }

        setPasswordChangeLoading(true);
        try {
            const response = await merchantAuthAPI.changePassword({
                current_password: passwordForm.currentPassword,
                new_password: passwordForm.newPassword,
                new_password_confirmation: passwordForm.confirmPassword,
                two_factor_code: user?.two_factor_enabled
                    ? passwordForm.twoFactorCode
                    : undefined,
            });
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                twoFactorCode: '',
            });
            setPasswordChangeSuccess(response.message);
            window.setTimeout(() => {
                const impersonation = isMerchantImpersonationActive();
                logout();
                if (!impersonation) navigate(cabinetPath('merchant', '/login'));
            }, 1000);
        } catch (err: any) {
            setPasswordChangeError(
                err.response?.data?.detail || 'Не удалось изменить пароль'
            );
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    if (!user) return null;
    return <div className="space-y-6">
        {/* Account Info */}
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600 dark:text-green-300" />
                    Информация об аккаунте
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Имя пользователя</Label>
                        <p className="break-words font-medium">{user.username}</p>
                    </div>
                    <div>
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Email</Label>
                        <p className="break-words font-medium">{user.email}</p>
                    </div>
                    <div>
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Компания</Label>
                        <p className="break-words font-medium">{user.company_name || '—'}</p>
                    </div>
                    <div>
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Статус</Label>
                        <div className="mt-1">
                            <Badge variant={user.is_active ? 'default' : 'secondary'}>
                                {user.is_active ? 'Активен' : 'Неактивен'}
                            </Badge>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Дата регистрации</Label>
                        <p className="break-words font-medium">
                            {new Date(user.created_at).toLocaleDateString('ru-RU')}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
        {/* Password settings */}
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <KeyRound className="h-5 w-5 text-green-600 dark:text-green-300" />
                    Смена пароля
                </CardTitle>
                <CardDescription>
                    После смены пароля потребуется повторно войти на всех устройствах
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form
                    onSubmit={handlePasswordChange}
                    className="max-w-xl space-y-5"
                >
                    <div className="space-y-2">
                        <Label htmlFor="currentMerchantPassword">
                            Текущий пароль
                        </Label>
                        <Input
                            id="currentMerchantPassword"
                            type="password"
                            autoComplete="current-password"
                            value={passwordForm.currentPassword}
                            onChange={(event) => {
                                setPasswordForm((current) => ({
                                    ...current,
                                    currentPassword: event.target.value,
                                }));
                                setPasswordChangeError(null);
                            }}
                            required
                            disabled={passwordChangeLoading || Boolean(passwordChangeSuccess)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="newMerchantPassword">
                            Новый пароль
                        </Label>
                        <Input
                            id="newMerchantPassword"
                            type="password"
                            autoComplete="new-password"
                            minLength={10}
                            value={passwordForm.newPassword}
                            onChange={(event) => {
                                setPasswordForm((current) => ({
                                    ...current,
                                    newPassword: event.target.value,
                                }));
                                setPasswordChangeError(null);
                            }}
                            required
                            disabled={passwordChangeLoading || Boolean(passwordChangeSuccess)}
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            Не менее 10 символов
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmMerchantPassword">
                            Повторите пароль
                        </Label>
                        <Input
                            id="confirmMerchantPassword"
                            type="password"
                            autoComplete="new-password"
                            minLength={10}
                            value={passwordForm.confirmPassword}
                            onChange={(event) => {
                                setPasswordForm((current) => ({
                                    ...current,
                                    confirmPassword: event.target.value,
                                }));
                                setPasswordChangeError(null);
                            }}
                            required
                            disabled={passwordChangeLoading || Boolean(passwordChangeSuccess)}
                        />
                    </div>

                    {user.two_factor_enabled && (
                        <div className="space-y-2">
                            <Label htmlFor="passwordChangeTwoFactorCode">
                                Код Google Authenticator
                            </Label>
                            <Input
                                id="passwordChangeTwoFactorCode"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="\d{6}"
                                maxLength={6}
                                placeholder="000000"
                                className="font-mono tracking-[0.3em]"
                                value={passwordForm.twoFactorCode}
                                onChange={(event) => {
                                    setPasswordForm((current) => ({
                                        ...current,
                                        twoFactorCode: event.target.value
                                            .replace(/\D/g, '')
                                            .slice(0, 6),
                                    }));
                                    setPasswordChangeError(null);
                                }}
                                required
                                disabled={passwordChangeLoading || Boolean(passwordChangeSuccess)}
                            />
                        </div>
                    )}

                    {passwordChangeError && (
                        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-600 dark:text-red-300">
                            {passwordChangeError}
                        </div>
                    )}
                    {passwordChangeSuccess && (
                        <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 text-sm text-green-700 dark:text-green-300">
                            {passwordChangeSuccess}. Выполняется выход…
                        </div>
                    )}

                    <Button
                        type="submit"
                        className="bg-slate-900 hover:bg-slate-800"
                        disabled={passwordChangeLoading || Boolean(passwordChangeSuccess)}
                    >
                        {passwordChangeLoading ? 'Изменение...' : 'Сменить пароль'}
                    </Button>
                </form>
            </CardContent>
        </Card>

        {/* Two-factor authentication */}
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-300" />
                            Двухфакторная аутентификация
                        </CardTitle>
                        <CardDescription className="mt-1">
                            Защитите вход кодом из Google Authenticator
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge variant={user.two_factor_enabled ? 'default' : 'secondary'}>
                            {user.two_factor_enabled ? 'Включена' : 'Выключена'}
                        </Badge>
                        <Switch
                            checked={user.two_factor_enabled}
                            onCheckedChange={handleTwoFactorToggle}
                            disabled={twoFactorLoading}
                            aria-label="Двухфакторная аутентификация"
                        />
                    </div>
                </div>
            </CardHeader>
            {twoFactorCardError && (
                <CardContent className="pt-0">
                    <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-600 dark:text-red-300">
                        {twoFactorCardError}
                    </div>
                </CardContent>
            )}
        </Card>

        <Dialog
            open={twoFactorDialog !== null}
            onOpenChange={(open) => {
                if (!open && !twoFactorLoading) closeTwoFactorDialog();
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {twoFactorDialog === 'setup'
                            ? 'Подключение Google Authenticator'
                            : 'Выключение 2FA'}
                    </DialogTitle>
                    <DialogDescription>
                        {twoFactorDialog === 'setup'
                            ? 'Отсканируйте QR-код и подтвердите подключение кодом из приложения.'
                            : 'Введите действующий код из Google Authenticator.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {twoFactorDialog === 'setup' && twoFactorSetup && (
                        <>
                            <div className="flex justify-center rounded-lg border bg-white p-4">
                                <QRCodeSVG
                                    value={twoFactorSetup.otpauth_uri}
                                    size={192}
                                    level="M"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ключ для ручного ввода</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        readOnly
                                        value={twoFactorSetup.manual_key}
                                        className="font-mono text-sm"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(
                                            twoFactorSetup.manual_key,
                                            'two_factor_key'
                                        )}
                                    >
                                        {copiedField === 'two_factor_key'
                                            ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" />
                                            : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="merchantTwoFactorCode">
                            Код из приложения
                        </Label>
                        <div className="relative">
                            <Smartphone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                                id="merchantTwoFactorCode"
                                value={twoFactorCode}
                                onChange={(event) => {
                                    setTwoFactorCode(
                                        event.target.value.replace(/\D/g, '').slice(0, 6)
                                    );
                                    setTwoFactorError(null);
                                }}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="\d{6}"
                                maxLength={6}
                                placeholder="000000"
                                className="pl-10 text-center font-mono text-lg tracking-[0.3em]"
                                autoFocus
                            />
                        </div>
                    </div>

                    {twoFactorError && (
                        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-600 dark:text-red-300">
                            {twoFactorError}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={closeTwoFactorDialog}
                        disabled={twoFactorLoading}
                    >
                        Отмена
                    </Button>
                    <Button
                        type="button"
                        onClick={
                            twoFactorDialog === 'setup'
                                ? handleEnableTwoFactor
                                : handleDisableTwoFactor
                        }
                        disabled={twoFactorLoading || twoFactorCode.length !== 6}
                        className={
                            twoFactorDialog === 'setup'
                                ? 'bg-green-600 hover:bg-green-700'
                                : ''
                        }
                    >
                        {twoFactorLoading
                            ? 'Проверка...'
                            : (twoFactorDialog === 'setup'
                                ? 'Включить 2FA'
                                : 'Выключить 2FA')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

    </div>;
};
